import React, { FC } from 'react';
import './DetailsBox.css';
import { DetailItem } from '@/Shared/Types';
import { Link } from 'react-router-dom';


interface DetailsBoxProps {
	data: DetailItem[];
	legend: string
}


/**
 * Component: DetailsBox
 *
 * Description:
 * Renders a simple, vertical list of details with optional links or click handlers.
 * Each detail displays a label and a value. The component is primarily used
 * for showing property or user information in a compact, readable format.
 *
 * Key Features:
 * - Supports clickable values via `onClick`.
 * - Supports navigation via `link` using `react-router-dom`'s `Link`.
 * - Graceful fallback for plain text when no link or click handler is provided.
 * - Fieldset and legend provide semantic grouping for accessibility.
 *
 * Props:
 * - data: DetailItem[]
 *   Array of objects containing `label`, `value`, and optionally `link` or `onClick`.
 * - legend: string
 *   Text displayed as the fieldset legend to provide context for the list.
 *
 * Responsibilities:
 * - Decide whether each value should be a plain text, a clickable span, or a navigable link.
 * - Render the list in a semantic, accessible fieldset.
 * - Maintain consistent styling for bold labels and thin value text.
 *
 * Author: Samano CASTRE
 * Date: Jan 17, 2026
 */
const DetailsBox: FC<DetailsBoxProps> = ({ data, legend }) => {

	return (
		<fieldset className="DetailsBox sm-scroll" data-testid="DetailsBox">
			<legend>{legend}</legend>
			{
				data.map((detail, index) =>
					<label className="details-page__label" key={(detail.link??"")+"-"+index}>
						<span className='bold-text'>{detail.label}: </span>
						{
							detail.link &&
							(<Link to={detail.link}>{detail.value}</Link>)
						}
						{
							detail.onClick &&
							(<span
								className="thin-text link"
								onClick={detail.onClick}>
								{detail.value}
							</span>)
						}
						{
							!(detail.link || detail.onClick) &&
							(<span className="thin-text"> {detail.value}</span>)
						}
					</label>
				)
			}
		</fieldset>
	);
}

export default DetailsBox;
